import React, { useState, useEffect, useMemo } from 'react';

const FeedbackTimeline = ({ 
  scoreGifs = null, // Pass an object like { 1: 'path/to/1.gif', 2: 'path/to/2.gif', etc. }
  showNumbers = true, // Set to false when using GIFs
  className = "" // Allow custom className for sizing
}) => {
  // Extended score sequence with new values - 2 of each for perfect balance
  const scoreSequence = [5, 4, 3, 2, 1, 'exclamation', 'google', 5, 4, 3, 2, 1, 'exclamation', 'google'];
  
  // State to track active bubbles
  const [activeBubbles, setActiveBubbles] = useState([]);
  
  // Generate schedule of when bubbles should appear
  const bubbleSchedule = useMemo(() => {
    const schedule = [];
    const cycleDuration = 30000; // 30 seconds in milliseconds
    
    // Generate random times for each score
    for (let i = 0; i < scoreSequence.length; i++) {
      schedule.push({
        id: i,
        score: scoreSequence[i],
        startTime: Math.random() * (cycleDuration - 8000), // Leave 8s for animation
        duration: 8000 // 8 seconds to cross
      });
    }
    
    // Sort by start time
    schedule.sort((a, b) => a.startTime - b.startTime);
    return schedule;
  }, []);

  useEffect(() => {
    const startTime = Date.now();
    
    const updateBubbles = () => {
      const currentTime = Date.now();
      const elapsed = (currentTime - startTime) % 30000; // 30-second cycle
      
      const newActiveBubbles = [];
      
      bubbleSchedule.forEach(bubble => {
        const bubbleAge = elapsed - bubble.startTime;
        
        // Check if bubble should be active (started but not finished)
        if (bubbleAge >= 0 && bubbleAge <= bubble.duration) {
          newActiveBubbles.push({
            ...bubble,
            age: bubbleAge
          });
        }
      });
      
      setActiveBubbles(newActiveBubbles);
    };
    
    const interval = setInterval(updateBubbles, 50); // Update every 50ms for smooth animation
    updateBubbles(); // Initial update
    
    return () => clearInterval(interval);
  }, [bubbleSchedule]);

  // Your brand colors (extended for new values)
  const scoreColors = {
    5: '#22c55e',
    4: '#84cc16',
    3: '#f59e0b',
    2: '#fb923c',
    1: '#ef4444',
    'exclamation': '#ef4444', // Red for exclamation
    'google': '#4285f4' // Google blue
  };

  const renderScoreContent = (score) => {
    if (scoreGifs && scoreGifs[score]) {
      return (
        <img 
          src={scoreGifs[score]} 
          alt={`Score ${score}`}
          className="object-contain"
          style={{
            width: '100%',    // 100% of the 54px bubble
            height: '100%',
            maxWidth: '54px',
            maxHeight: '54px'
          }}
        />
      );
    }
    
    // Fallback content when no GIFs provided
    if (score === 'exclamation') {
      return '❗';
    }
    if (score === 'google') {
      return 'G';
    }
    return showNumbers ? score : '';
  };

  const getBubbleStyle = (bubble) => {
    const progress = bubble.age / bubble.duration;
    const left = -100 + (850 * progress); // Move from -100px to 750px
    
    let opacity = 1;
    let blur = 0;
    let scale = 1;
    
    if (progress < 0.25) {
      // Blur in phase
      const blurProgress = progress / 0.25;
      opacity = blurProgress;
      blur = 4 * (1 - blurProgress);
      scale = 0.8 + (0.2 * blurProgress);
    } else if (progress > 0.9) {
      // Blur out phase
      const blurProgress = (progress - 0.9) / 0.1;
      opacity = 1 - (0.3 * blurProgress);
      blur = 3 * blurProgress;
      scale = 1 - (0.2 * blurProgress);
    }
    
    return {
      left: `${left}px`,
      opacity,
      transform: `scale(${scale})`,
      filter: `blur(${blur}px)`,
      transition: 'none' // No CSS transitions, we're handling the animation
    };
  };

  return (
    <div className={`relative ${className}`}>
      <style jsx>{`
        .dashboard {
            width: 700px;
            height: 450px;
            background: #F8FAFC;
            border-radius: 16px;
            box-shadow: 0 20px 40px rgba(0,0,0,0.1);
            position: relative;
            overflow: hidden;
            border: 2px solid #e2e8f0;
            margin: 0 auto;
            min-width: 300px; /* Prevent it from getting too small */
        }
        
        .timeline-container {
            position: relative;
            height: 400px;
            margin: 40px 30px;
        }
        
        .timeline-axis {
            position: absolute;
            bottom: 60px;
            left: 0;
            right: 0;
            height: 2px;
            background: #e1e5e9;
        }
        
        .time-labels {
            position: absolute;
            bottom: 35px;
            left: 0;
            right: 0;
            display: flex;
            justify-content: space-between;
            font-size: 12px;
            color: #95a5a6;
        }
        
        .score-bubble {
            position: absolute;
            width: 60px;
            height: 60px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 600;
            font-size: 20px;
            color: white;
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
            bottom: 50px;
        }
        
        .score-1 { background: #F8FAFC; border: 2px solid #e2e8f0; }
        .score-2 { background: #F8FAFC; border: 2px solid #e2e8f0; }
        .score-3 { background: #F8FAFC; border: 2px solid #e2e8f0; }
        .score-4 { background: #F8FAFC; border: 2px solid #e2e8f0; }
        .score-5 { background: #F8FAFC; border: 2px solid #e2e8f0; }
        .score-exclamation { background: #F8FAFC; border: 2px solid #e2e8f0; }
        .score-google { background: #F8FAFC; border: 2px solid #e2e8f0; }
        
        .stats-container {
            position: absolute;
            top: 20px;
            right: 40px;
            width: 90%;
            background: #F8FAFC;
            border-radius: 12px;
            padding: 20px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.08);
            border: 2px solid #e2e8f0;
        }
        
        .chart-title {
            font-size: 14px;
            font-weight: 600;
            color: #2c3e50;
            margin-bottom: 15px;
            text-align: center;
        }
        
        .bar-item {
            display: flex;
            align-items: center;
            margin-bottom: 12px;
            position: relative;
        }
        
        .bar-label {
            font-size: 14px;
            font-weight: 600;
            color: #2c3e50;
            width: 20px;
            text-align: center;
        }
        
        .bar-container {
            flex: 1;
            height: 24px;
            background: #f1f3f4;
            border-radius: 12px;
            margin-left: 10px;
            position: relative;
            overflow: hidden;
        }
        
        .bar-fill {
            height: 100%;
            border-radius: 12px;
            transition: width 0.8s ease-out;
            position: relative;
        }
        
        .bar-5 { background: #22c55e; }
        .bar-4 { background: #84cc16; }
        .bar-3 { background: #f59e0b; }
        .bar-2 { background: #fb923c; }
        .bar-1 { background: #ef4444; }
        .bar-exclamation { background: #ef4444; }
        .bar-google { background: #4285f4; }
        
        /* Individual bar animations - updated for 7 values */
        .bar-fill.bar-5 {
            animation: bar5Animation 30s ease-out infinite;
        }
        .bar-fill.bar-4 {
            animation: bar4Animation 30s ease-out infinite;
        }
        .bar-fill.bar-3 {
            animation: bar3Animation 30s ease-out infinite;
        }
        .bar-fill.bar-2 {
            animation: bar2Animation 30s ease-out infinite;
        }
        .bar-fill.bar-1 {
            animation: bar1Animation 30s ease-out infinite;
        }
        .bar-fill.bar-exclamation {
            animation: barExclamationAnimation 30s ease-out infinite;
        }
        .bar-fill.bar-google {
            animation: barGoogleAnimation 30s ease-out infinite;
        }
        
        @keyframes bar5Animation {
            0% { width: 80%; }
            10% { width: 100%; }
            20% { width: 100%; }   
            30% { width: 100%; }   
            40% { width: 80%; }
            50% { width: 60%; }
            60% { width: 80%; }
            70% { width: 80%; }
            80% { width: 80%; }
            90% { width: 80%; }
            100% { width: 80%; }
        }
        
        @keyframes bar4Animation {
            0% { width: 70%; }
            10% { width: 70%; }    
            20% { width: 90%; }
            30% { width: 90%; }    
            40% { width: 70%; }    
            50% { width: 50%; }
            60% { width: 50%; }    
            70% { width: 70%; }
            80% { width: 70%; }
            90% { width: 70%; }
            100% { width: 70%; }
        }
        
        @keyframes bar3Animation {
            0% { width: 50%; }
            10% { width: 50%; }    
            20% { width: 50%; }    
            30% { width: 70%; }
            40% { width: 70%; }    
            50% { width: 90%; }
            60% { width: 70%; }    
            70% { width: 70%; }    
            80% { width: 50%; }
            90% { width: 50%; }
            100% { width: 50%; }
        }
        
        @keyframes bar2Animation {
            0% { width: 30%; }
            10% { width: 30%; }    
            20% { width: 30%; }    
            30% { width: 30%; }    
            40% { width: 50%; }
            50% { width: 70%; }
            60% { width: 50%; }    
            70% { width: 50%; }    
            80% { width: 50%; }    
            90% { width: 30%; }
            100% { width: 30%; }
        }
        
        @keyframes bar1Animation {
            0% { width: 20%; }
            10% { width: 20%; }    
            20% { width: 20%; }    
            30% { width: 20%; }    
            40% { width: 20%; }    
            50% { width: 40%; }
            60% { width: 60%; }
            70% { width: 40%; }    
            80% { width: 40%; }    
            90% { width: 40%; }    
            100% { width: 20%; }
        }

        @keyframes barExclamationAnimation {
            0% { width: 10%; }
            20% { width: 10%; }
            40% { width: 30%; }
            60% { width: 20%; }
            80% { width: 15%; }
            100% { width: 10%; }
        }

        @keyframes barGoogleAnimation {
            0% { width: 5%; }
            20% { width: 15%; }
            40% { width: 25%; }
            60% { width: 20%; }
            80% { width: 10%; }
            100% { width: 5%; }
        }
        
        .average-line {
            position: absolute;
            bottom: 110px;
            left: 0;
            right: 200px;
            height: 2px;
            background: linear-gradient(90deg, #667eea, #764ba2);
            opacity: 0.7;
            animation: fluctuate 30s ease-in-out infinite;
        }
        
        @keyframes fluctuate {
            0% { transform: translateY(0px); }
            25% { transform: translateY(-10px); }
            50% { transform: translateY(5px); }
            75% { transform: translateY(-15px); }
            100% { transform: translateY(0px); }
        }
        
        .chart-grid {
            position: absolute;
            left: 0;
            right: 200px;
            top: 0;
            bottom: 60px;
            opacity: 0.1;
        }
        
        .grid-line {
            position: absolute;
            left: 0;
            right: 0;
            height: 1px;
            background: #bdc3c7;
        }
        
        .grid-line:nth-child(1) { top: 20%; }
        .grid-line:nth-child(2) { top: 40%; }
        .grid-line:nth-child(3) { top: 60%; }
        .grid-line:nth-child(4) { top: 80%; }
        
        .pulse-indicator {
            position: absolute;
            top: 20px;
            right: 20px;
            width: 12px;
            height: 12px;
            background: #2ecc71;
            border-radius: 50%;
            animation: pulse 2s ease-in-out infinite;
        }
        
        .pulse-indicator::after {
            content: '';
            position: absolute;
            top: -2px;
            left: -2px;
            width: 16px;
            height: 16px;
            border: 2px solid #2ecc71;
            border-radius: 50%;
            animation: ripple 2s ease-in-out infinite;
        }
        
        @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
        }
        
        @keyframes ripple {
            0% {
                transform: scale(1);
                opacity: 1;
            }
            100% {
                transform: scale(2);
                opacity: 0;
            }
        }
        
        .live-label {
            position: absolute;
            top: 22px;
            right: 40px;
            font-size: 12px;
            color: #2ecc71;
            font-weight: 600;
        }

        /* MOBILE RESPONSIVE FIXES - Minimal Impact */
        
        /* Tablets and smaller desktops - Fixed breakpoint */
        @media (max-width: 980px) {
            .dashboard {
                width: 100%;
                max-width: 700px;
                margin: 0 auto;
                box-sizing: border-box;
            }
        }

        /* Medium screens - smooth transition */
        @media (max-width: 900px) {
            .dashboard {
                width: 100%;
                max-width: 650px;
            }
        }

        /* Large mobile and small tablets */
        @media (max-width: 800px) {
            .dashboard {
                width: 100%;
                max-width: 100%;
                height: 380px;
                margin: 0;
                border-radius: 12px;
            }
            
            .timeline-container {
                margin: 30px 20px;
                height: 320px;
            }
            
            .stats-container {
                width: 130px;
                padding: 15px;
                right: 15px;
                top: 15px;
            }
            
            .chart-title {
                font-size: 12px;
                margin-bottom: 10px;
            }
            
            .bar-item {
                margin-bottom: 8px;
            }
            
            .bar-label {
                font-size: 12px;
                width: 15px;
            }
            
            .bar-container {
                height: 18px;
                margin-left: 8px;
            }
            
            .score-bubble {
                width: 35px;
                height: 35px;
                font-size: 16px;
                bottom: 40px;
            }
            
            .time-labels {
                font-size: 10px;
                bottom: 25px;
            }
            
            .timeline-axis {
                bottom: 50px;
            }
            
            .average-line {
                bottom: 90px;
                right: 150px;
            }
            
            .chart-grid {
                right: 150px;
                bottom: 50px;
            }
            
            .pulse-indicator {
                width: 10px;
                height: 10px;
                top: 12px;
                right: 12px;
            }
            
            .live-label {
                font-size: 10px;
                top: 15px;
                right: 28px;
            }
        }

        /* Mobile phones */
        @media (max-width: 480px) {
            .dashboard {
                height: 320px;
                border-radius: 8px;
            }
            
            .timeline-container {
                margin: 20px 15px;
                height: 280px;
            }
            
            .stats-container {
                width: 110px;
                padding: 12px;
                right: 10px;
                top: 10px;
            }
            
            .chart-title {
                font-size: 11px;
                margin-bottom: 8px;
            }
            
            .bar-item {
                margin-bottom: 6px;
            }
            
            .bar-label {
                font-size: 11px;
                width: 12px;
            }
            
            .bar-container {
                height: 16px;
                margin-left: 6px;
            }
            
            .score-bubble {
                width: 30px;
                height: 30px;
                font-size: 14px;
                bottom: 35px;
            }
            
            .time-labels {
                font-size: 9px;
                bottom: 20px;
            }
            
            .time-labels span:nth-child(2),
            .time-labels span:nth-child(4) {
                display: none;
            }
            
            .timeline-axis {
                bottom: 45px;
            }
            
            .average-line {
                bottom: 80px;
                right: 130px;
            }
            
            .chart-grid {
                right: 130px;
                bottom: 45px;
            }
        }

        /* Very small phones */
        @media (max-width: 360px) {
            .dashboard {
                height: 280px;
            }
            
            .timeline-container {
                margin: 15px 10px;
                height: 250px;
            }
            
            .stats-container {
                width: 95px;
                padding: 10px;
                right: 8px;
                top: 8px;
            }
            
            .chart-title {
                font-size: 10px;
                margin-bottom: 6px;
            }
            
            .bar-item {
                margin-bottom: 5px;
            }
            
            .bar-label {
                font-size: 10px;
                width: 10px;
            }
            
            .bar-container {
                height: 14px;
                margin-left: 5px;
            }
            
            .score-bubble {
                width: 25px;
                height: 25px;
                font-size: 12px;
                bottom: 30px;
            }
            
            .time-labels {
                font-size: 8px;
                bottom: 15px;
            }
            
            .timeline-axis {
                bottom: 40px;
            }
            
            .average-line {
                bottom: 70px;
                right: 110px;
            }
            
            .chart-grid {
                right: 110px;
                bottom: 40px;
            }
            
            .pulse-indicator {
                width: 8px;
                height: 8px;
            }
            
            .live-label {
                font-size: 9px;
                top: 12px;
                right: 22px;
            }
        }
      `}</style>

      <div className="dashboard">
        <div className="pulse-indicator"></div>
        <div className="live-label">LIVE</div>
        
        <div className="timeline-container">
          <div className="chart-grid">
            <div className="grid-line"></div>
            <div className="grid-line"></div>
            <div className="grid-line"></div>
            <div className="grid-line"></div>
          </div>
          
          <div className="average-line"></div>
          
          <div className="timeline-axis"></div>
          <div className="time-labels">
            <span>25s ago</span>
            <span>20s ago</span>
            <span>15s ago</span>
            <span>10s ago</span>
            <span>5s ago</span>
            <span>now</span>
          </div>
          
          {/* Dynamically rendered active bubbles */}
          {activeBubbles.map((bubble) => (
            <div
              key={`${bubble.id}-${Math.floor(bubble.age / 30000)}`}
              className={`score-bubble score-${bubble.score}`}
              style={getBubbleStyle(bubble)}
            >
              {renderScoreContent(bubble.score)}
            </div>
          ))}
        </div>
        
        <div className="stats-container">
          <div className="chart-title">Response Distribution</div>
          
          {[5, 4, 3, 2, 1].map((score) => (
            <div key={score} className="bar-item">
              <div className="bar-label">{score}</div>
              <div className="bar-container">
                <div className={`bar-fill bar-${score}`}></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FeedbackTimeline;